import Box from "@cloudscape-design/components/box"
import PieChart, { type PieChartProps } from "@cloudscape-design/components/pie-chart"

interface StatusDonutProps {
  data: PieChartProps.Datum[]
  innerMetricValue: string
  innerMetricDescription: string
  loading: boolean
  ariaLabel: string
  size?: PieChartProps["size"]
  /**
   * Scale the chart to fill the parent's height instead of the fixed size presets.
   */
  fitHeight?: boolean
  /**
   * Suppress the per-segment descriptions the chart otherwise reserves vertical space for. Opt-in
   * (default keeps Cloudscape's behaviour) so only callers that don't use descriptions can reclaim
   * that space for a larger ring.
   */
  hideDescriptions?: boolean
  /**
   * Rendered in the footer of a segment's detail popover — e.g. a link to the filtered list for
   * that status. Omitted by default so the study-level panel stays non-interactive.
   */
  detailPopoverFooter?: PieChartProps<PieChartProps.Datum>["detailPopoverFooter"]
}

/**
 * A small status-breakdown donut chart with a centred inner metric. Shared between the portfolio
 * dashboard health panel and the study-level health panel so both render identically.
 *
 * Callers filter out zero-value slices, so `data` is empty when a study/portfolio has no DTAs or
 * runs yet; the `empty` slot handles that case instead of rendering a broken chart.
 */
const StatusDonut = ({
  data,
  innerMetricValue,
  innerMetricDescription,
  loading,
  ariaLabel,
  size = "small",
  fitHeight = false,
  hideDescriptions = false,
  detailPopoverFooter,
}: StatusDonutProps) => (
  <PieChart
    variant="donut"
    size={size}
    fitHeight={fitHeight}
    detailPopoverFooter={detailPopoverFooter}
    hideFilter
    hideTitles
    hideDescriptions={hideDescriptions}
    data={data}
    innerMetricValue={innerMetricValue}
    innerMetricDescription={innerMetricDescription}
    statusType={loading ? "loading" : "finished"}
    ariaLabel={ariaLabel}
    empty={
      <Box textAlign="center" color="text-body-secondary">
        No data yet
      </Box>
    }
  />
)

export default StatusDonut
